import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";

import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { Search, Download } from "lucide-react";
import { toast } from "sonner";
import { orderService } from "@/services/order";
import { DateUtils } from "@/utils/dateUtils";
import { GetOrdersParams, Order, OrderStatusType } from "@/types/order";
import { Textarea } from "@/components/ui/textarea";

// 类型
type QueryField = "phone" | "order_no";
type TimeQueryField = "created_at" | "pay_time" | "refund_time";
type VIPTimeField = "all" | "1" | "2";
type giftField = "0" | "1" | "-1";
const OrderStatus = {
  all: "全部",
  pending: "待支付",
  paid: "已支付",
  completed: "已完成",
  failed: "支付失败",
  refunded: "已退款",
  refunding: "退款中",
  canceled: "已取消",
  refund_applied: "退款申请中",
};
const ExportTimeQuery = {
  created_at: "下单",
  pay_time: "付款",
  refund_time: "退款",
};
const formatPriceToYuan = (priceInCents: number | undefined) =>
  (priceInCents || 0) / 100;
const formatPriceToCents = (priceInYuan: number | string | undefined) =>
  Math.round(Number(priceInYuan || 0) * 100);

export default function OrderManagement() {
  // 分页
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // 数据
  const [loading, setLoading] = useState(true);
  // 保存原始数据
  const [orders, setOrders] = useState<Order[]>([]);
  // 保存排序过的数据
  const [sortedOrders, setSortedOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);

  // 1) 条件查询：字段 + 输入
  const [field, setField] = useState<QueryField>("phone");
  const [keyword, setKeyword] = useState("");

  // 2) 条件查询-时间筛选
  const [time, setTime] = useState<TimeQueryField>("created_at");
  const [timeStart, setTimeStart] = useState<string>("");
  const [timeEnd, setTimeEnd] = useState<string>("");

  // 3) 订单状态
  const [status, setStatus] = useState<OrderStatusType | "all">("all");

  // 4) VIP
  const [vip, setVip] = useState<VIPTimeField>("all");

  // 5) 赠品
  const [gift, setGift] = useState<giftField>("0");

  // 导出弹窗相关状态
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [processingExport, setProcessingExport] = useState(false);

  // 退款弹窗相关状态
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [refundPrice, setRefundPrice] = useState<string | undefined>();
  const [refundReason, setRefundReason] = useState("");
  const [processingRefund, setProcessingRefund] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  // UI状态 -> 接口参数映射
  const buildRequestParams = () => {
    const params: GetOrdersParams = {
      page,
      page_size: pageSize,
    };

    // 条件查询：根据选中的字段名赋值
    const kw = keyword.trim();
    if (kw) {
      if (field === "phone") {
        params.phone = kw;
      } else if (field === "order_no") {
        params.order_no = kw;
      }
    }
    if (time) {
      const getKey = {
        created_at: "create_time",
        pay_time: "pay_time",
        refund_time: "refund_time",
      };
      if (timeStart) {
        params[
          `start_${getKey[time] as "create_time" | "pay_time" | "refund_time"}`
        ] = DateUtils.toChineseISOString(timeStart, false);
      }
      if (timeEnd) {
        params[
          `end_${getKey[time] as "create_time" | "pay_time" | "refund_time"}`
        ] = DateUtils.toChineseISOString(timeEnd, true);
      }
    }

    if (status && status !== "all") params.status = status;
    if (vip && vip !== "all") params.vip_duration = Number(vip);
    if (gift && gift !== "0") params.is_gift = Number(gift) as -1 | 1;

    return params;
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = buildRequestParams();
      const res = await orderService.getOrders(params);
      // 更新映射逻辑以匹配API返回的结构
      const list = (res as { list?: Order[]; total?: number }).list ?? [];
      const totalCount = (res as { list?: Order[]; total?: number }).total ?? 0;
      setOrders(list);
      setSortedOrders(list);
      setTotal(totalCount);
    } catch (e) {
      console.error(e);
      toast.error("获取订单列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setLoading(false);
    }
  };

  // 退款请求
  const refund = async () => {
    setProcessingRefund(true);
    try {
      // 退款接口
      if (selectedOrder) {
        await orderService.refundForUser({
          id: selectedOrder?.id,
          params: {
            amount: formatPriceToCents(refundPrice),
            order_no: selectedOrder?.order_no,
            reason: refundReason,
          },
        });
      }

      fetchOrders();
    } catch (e) {
      console.error(e);
      toast.error("退款失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setProcessingRefund(false);
      setRefundDialogOpen(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchOrders();
  };

  const resetFilters = () => {
    setKeyword("");
    setTimeStart("");
    setTimeEnd("");
    setTime("created_at");
    setStatus("all");
    setVip("all");
    setGift("-1");
    setPage(1);
  };

  // 打开导出确认弹窗
  const handleExport = () => {
    setExportDialogOpen(true);
  };

  // 执行实际导出操作
  const confirmExport = async () => {
    setProcessingExport(true);
    try {
      const params = buildRequestParams();
      const blob = await orderService.exportOrders(params);

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `订单记录_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();

      // 清理
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("导出成功", {
        description: "订单记录已成功导出",
      });

      // 关闭弹窗
      setExportDialogOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("导出失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setProcessingExport(false);
    }
  };

  const placeholderByField: Record<QueryField, string> = {
    phone: "请输入用户手机号",
    order_no: "请输入订单号",
  };

  const renderFilterSummary = () => {
    if (field === "phone" && keyword) return `用户手机号: ${keyword}`;
    if (field === "order_no" && keyword) return `订单号: ${keyword}`;
    return "无特定条件";
  };

  // 打开退款操作弹窗
  const openRefundDialog = (order: Order) => {
    setSelectedOrder(order);
    setRefundPrice(String(formatPriceToYuan(order.pay_price)));
    setRefundReason(order.refund_reason || "");
    setRefundDialogOpen(true);
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">订单管理</h2>
      </div>
      {/* 筛选区 - 现在分为两行 */}
      <div className="rounded-lg border p-4 space-y-4">
        {/* 第一行 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* 1. 条件查询 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">条件查询</label>
            <div className="flex gap-2">
              <Select
                value={field}
                onValueChange={(v: QueryField) => setField(v)}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="选择字段" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">用户手机号</SelectItem>
                  <SelectItem value="order_no">订单号</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder={placeholderByField[field]}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="flex-1"
              />
            </div>
          </div>
          {/* 2. 条件查询-时间 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">时间</label>
            <div className="flex gap-3">
              <Select
                value={time}
                onValueChange={(v: TimeQueryField) => setTime(v)}
              >
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="选择字段" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">下单时间</SelectItem>
                  <SelectItem value="pay_time">付款时间</SelectItem>
                  <SelectItem value="refund_time">退款时间</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={timeStart}
                onChange={(e) => setTimeStart(e.target.value)}
              />
              <Input
                type="date"
                value={timeEnd}
                onChange={(e) => setTimeEnd(e.target.value)}
              />
            </div>
          </div>
        </div>
        {/* 第二行 */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* 3. 订单状态 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">订单状态</label>
            <div className="flex gap-2">
              <Select
                value={status}
                onValueChange={(v: OrderStatusType) => setStatus(v)}
              >
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="选择订单状态" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(OrderStatus).map(([key, value]) => {
                    return (
                      <SelectItem key={key} value={key}>
                        {value}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* 4.VIP */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">VIP</label>
            <div className="flex gap-2">
              <Select
                value={vip}
                onValueChange={(v: VIPTimeField) => setVip(v)}
              >
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="选择订单状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  {/* 字段待更新 */}
                  <SelectItem value="1">1年VIP</SelectItem>
                  <SelectItem value="2">2年VIP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* 5. 赠品*/}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">赠品</label>
            <div className="flex gap-2">
              <Select value={gift} onValueChange={(v: giftField) => setGift(v)}>
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="选择赠品" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">不限</SelectItem>
                  {/* 字段待更新 */}
                  <SelectItem value="1">有赠品</SelectItem>
                  <SelectItem value="-1">无赠品</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* 6. 操作按钮组 */}
          <div className="flex flex-col gap-2 justify-end">
            <label className="text-sm text-gray-600 opacity-0">操作</label>
            <div className="flex gap-2 justify-end">
              <Button className="w-24" variant="outline" onClick={resetFilters}>
                重置
              </Button>
              <Button className="w-24" onClick={handleSearch}>
                <Search className="mr-2 h-4 w-4" />
                查询
              </Button>
              <Button
                className="w-24"
                variant="secondary"
                onClick={handleExport}
              >
                <Download className="mr-2 h-4 w-4" />
                导出
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* 列表：可滚动容器 */}
      <div className="rounded-md border overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">id</TableHead>
              <TableHead className="whitespace-nowrap">订单号</TableHead>
              <TableHead className="whitespace-nowrap">商品名称</TableHead>
              <TableHead className="whitespace-nowrap">赠品</TableHead>
              <TableHead className="whitespace-nowrap">购买手机号</TableHead>
              <TableHead className="whitespace-nowrap">昵称</TableHead>
              <TableHead className="whitespace-nowrap">下单时间</TableHead>
              <TableHead className="whitespace-nowrap">支付金额</TableHead>
              <TableHead className="whitespace-nowrap">设备</TableHead>
              <TableHead className="whitespace-nowrap">支付时间</TableHead>
              <TableHead className="whitespace-nowrap">申请退款时间</TableHead>
              <TableHead className="whitespace-nowrap">退款时间</TableHead>
              <TableHead className="whitespace-nowrap">退款原因</TableHead>
              <TableHead className="whitespace-nowrap">订单状态</TableHead>
              <TableHead className="whitespace-nowrap">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={13} className="h-24 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} className="h-24 text-center">
                  没有找到订单记录
                </TableCell>
              </TableRow>
            ) : (
              sortedOrders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="whitespace-nowrap">
                    {order.id || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.order_no || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.product_name || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.gift_duration
                      ? `${order.gift_duration}${order.gift_unit}`
                      : "无"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.phone || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.receiver || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.created_at
                      ? new Date(order.created_at).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatPriceToYuan(order.pay_price)}元
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.pay_platform?
                    order.pay_platform === "ios"?"苹果":"安卓"
                    :""}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.pay_time
                      ? new Date(order.pay_time).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.apply_refund_time
                      ? new Date(order.apply_refund_time).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.refund_time
                      ? new Date(order.refund_time).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell className="max-w-[150px] truncate">
                    {order.refund_reason || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {OrderStatus[order.status] ? (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ["pending", "refunding"].includes(order.status)
                            ? "bg-yellow-100 text-yellow-800"
                            : ["paid", "completed", "canceled"].includes(
                                order.status
                              )
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {OrderStatus[order.status] || "-"}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {["paid", "completed"].includes(order.status) ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {/* <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openRefundDialog(order)}
                            >
                              <p className="h-4 w-4 text-red-500">申请退款</p>
                            </Button> */}
                          </TooltipTrigger>
                        </Tooltip>
                      </TooltipProvider>
                    ) : order.status === "refund_applied" ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openRefundDialog(order)}
                            >
                              <p className="h-4 w-4 text-red-500">退款审核</p>
                            </Button>
                          </TooltipTrigger>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {/* 导出确认弹窗 */}
      <Dialog
        open={exportDialogOpen}
        onOpenChange={(open) => !open && setExportDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>确认导出订单记录</DialogTitle>
            <DialogDescription>
              将当前筛选条件下的订单记录导出为Excel文件
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">导出记录数量:</span>
                <span className="font-medium text-blue-600">{total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">筛选条件:</span>
                <span className="text-gray-600">{renderFilterSummary()}</span>
              </div>
              {timeStart && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {ExportTimeQuery[time]}开始时间:
                  </span>
                  <span className="text-gray-600">{timeStart}</span>
                </div>
              )}
              {timeEnd && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {ExportTimeQuery[time]}结束时间:
                  </span>
                  <span className="text-gray-600">{timeEnd}</span>
                </div>
              )}
            </div>
            {status !== "all" && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">订单状态:</span>
                <span className="text-gray-600">{OrderStatus[status]}</span>
              </div>
            )}
            {vip !== "all" && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">VIP:</span>
                <span className="text-gray-600">
                  {vip == "1" ? "1年VIP" : "2年VIP"}
                </span>
              </div>
            )}
            {gift !== "-1" && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">赠品:</span>
                <span className="text-gray-600">
                  {gift == "0" ? "无赠品" : "有赠品"}
                </span>
              </div>
            )}
            <p className="text-sm text-gray-500">
              导出的Excel文件将包含所有当前筛选条件下的订单记录数据
            </p>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button onClick={confirmExport} disabled={processingExport}>
              {processingExport ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                  导出中...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  确认导出
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* 退款确认弹窗 */}
      {selectedOrder && (
        <Dialog
          open={refundDialogOpen}
          onOpenChange={(open) => !open && setRefundDialogOpen(false)}
        >
          <DialogContent className="sm:max-w-md bg-white">
            <DialogHeader>
              <DialogTitle>退款</DialogTitle>
              <DialogDescription>确定为该订单退款吗?</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">手机号:</span>
                  <span className="text-gray-600">{selectedOrder.phone}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">订单号:</span>
                  <span className="text-gray-600">
                    {selectedOrder.order_no}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">商品名称:</span>
                  <span className="text-gray-600">
                    {selectedOrder.product_name}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">赠品:</span>
                  <span className="text-gray-600">{`${
                    selectedOrder.gift_duration
                  }${selectedOrder.gift_unit === "year" ? "年" : "月"}`}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">下单时间:</span>
                  <span className="text-gray-600">
                    {DateUtils.formatDateTime(
                      new Date(selectedOrder.created_at)
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">退款时间:</span>
                  <span className="text-gray-600">
                    {selectedOrder.apply_refund_time
                      ? DateUtils.formatDateTime(
                          new Date(selectedOrder.apply_refund_time)
                        )
                      : "-"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">退款金额(元):</span>
                  <span className="text-gray-600">
                    <Input
                      placeholder="请输入退款金额"
                      type="number"
                      value={refundPrice}
                      onChange={(e) => setRefundPrice(e.target.value)}
                    />
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">退款原因:</span>
                  <span className="text-gray-600">
                    <Textarea
                      placeholder="请输入退款原因"
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      rows={3}
                    />
                  </span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">取消</Button>
              </DialogClose>
              <Button
                // onClick={updateCourseStatus}
                onClick={refund}
                disabled={
                  processingRefund ||
                  !refundReason.trim() ||
                  !String(refundPrice).trim()
                }
                // variant={
                //   selectedCourse.status === 1 ? "destructive" : "default"
                // }
              >
                {processingRefund ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                    处理中...
                  </>
                ) : (
                  "退款"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {!loading && orders.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 whitespace-nowrap">
            共 {total} 条订单记录，第 {page} 页 / 共 {totalPages} 页
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => page > 1 && setPage(page - 1)}
                  className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <PaginationItem key={i}>
                    <PaginationLink
                      isActive={page === pageNum}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => page < totalPages && setPage(page + 1)}
                  className={
                    page >= totalPages ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
